import { useScrollContext } from "@navigations/utils/ScrollContext";
import { Platform } from "react-native";
import {
  runOnJS,
  useAnimatedScrollHandler,
  useSharedValue,
  withTiming,
  cancelAnimation,
} from "react-native-reanimated";
import { useTheme } from "styled-components/native";
import { NativeSyntheticEvent, NativeScrollEvent } from "react-native";
import { useRef } from "react";
import Routes from "@navigations/Routes";

const clamp = (value: number, min: number, max: number) => {
  "worklet";
  return Math.max(min, Math.min(max, value));
};

const SCROLL_DISTANCE = 200;
const NEAR_TOP_THRESHOLD = 50;
const SUSTAINED_THRESHOLD = 1;
const WEB_SUSTAINED_THRESHOLD = 5;
const SMOOTHING_FACTOR = 0.9;

const useHideNavsOnScroll = () => {
  const [
    scrollValue,
    _,
    __,
    ___,
    ____,
    ______,
    previousScrollValue,
    binaryScrollValue,
    searchResultSavedOffset,
  ] = useScrollContext();

  const yOffset = useSharedValue(0);
  const smoothedScrollDelta = useSharedValue(0);
  const hasTriggered = useSharedValue(false);
  const previousContentHeight = useSharedValue(0);
  const theme = useTheme();
  const CURRENT_SUSTAINED_THRESHOLD = theme.isLargeScreen
    ? WEB_SUSTAINED_THRESHOLD
    : SUSTAINED_THRESHOLD;
  const scrollHandler = (
    defaultScrollDistance?: number,
    onNearEnd?: () => void,
    onViewScroll?: (y: number) => void,
    _dependencies: any[] = [],
    routeKey?: Routes
  ) =>
    useAnimatedScrollHandler((event) => {
      const scrollDistance = defaultScrollDistance ?? SCROLL_DISTANCE;
      const { y } = event.contentOffset;

      yOffset.value = y;

      if (y < 0 || y > event.contentSize.height) {
        return;
      }

      if (
        event.contentSize.height - event.layoutMeasurement.height <
        scrollDistance
      ) {
        if (y <= NEAR_TOP_THRESHOLD) {
          scrollValue.value = withTiming(0, { duration: 200 });
          binaryScrollValue.value = withTiming(0, { duration: 200 });
          smoothedScrollDelta.value = 0;
        }
        return;
      }

      if (Platform.OS === "web" && event.velocity && event.velocity?.y === 0) {
        return;
      }

      const currentScrollDelta = y - previousScrollValue.value;

      smoothedScrollDelta.value =
        SMOOTHING_FACTOR * smoothedScrollDelta.value +
        (1 - SMOOTHING_FACTOR) * currentScrollDelta;

      scrollValue.value = clamp(
        scrollValue.value + smoothedScrollDelta.value / scrollDistance,
        0,
        1
      );
      if (y <= NEAR_TOP_THRESHOLD) {
        scrollValue.value = withTiming(0, { duration: 200 });
        binaryScrollValue.value = withTiming(0, { duration: 200 });
        smoothedScrollDelta.value = 0;
      }

      if (Math.abs(smoothedScrollDelta.value) >= CURRENT_SUSTAINED_THRESHOLD) {
        binaryScrollValue.value = smoothedScrollDelta.value > 0 ? 1 : 0;
      }

      if (
        Platform.OS === "web" &&
        y < NEAR_TOP_THRESHOLD &&
        (event.velocity?.y ?? 0) < 0
      ) {
        binaryScrollValue.value = withTiming(0, { duration: 200 });
      }

      const distanceFromBottom =
        event.contentSize.height / 2 < y + event.layoutMeasurement.height;

      if (distanceFromBottom && !hasTriggered.value) {
        hasTriggered.value = true;
        if (onNearEnd) {
          runOnJS(onNearEnd)();
        }
      }

      if (event.contentSize.height !== previousContentHeight.value) {
        hasTriggered.value = false;
        previousContentHeight.value = event.contentSize.height;
      }

      previousScrollValue.value = y;
      if (y !== 0 && routeKey === Routes.SearchResultScreen) {
        searchResultSavedOffset.value = y;
      }
      if (onViewScroll) runOnJS(onViewScroll)(y);
    });

  const smoothedScrollDeltaRef = useRef(0);
  const hasTriggeredRef = useRef(false);
  const previousContentHeightRef = useRef(0);

  const jsScrollHandler = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
    defaultScrollDistance?: number,
    onNearEnd?: () => void,
    routeKey?: Routes
  ) => {
    const { contentOffset, layoutMeasurement, contentSize, velocity } =
      event.nativeEvent;
    const y = contentOffset.y;
    const scrollDistance = defaultScrollDistance ?? SCROLL_DISTANCE;

    if (y < 0 || y > contentSize.height) {
      return;
    }

    if (contentSize.height - layoutMeasurement.height < scrollDistance) {
      if (y <= NEAR_TOP_THRESHOLD) {
        scrollValue.value = 0;
        binaryScrollValue.value = 0;
        smoothedScrollDeltaRef.current = 0;
      }
      return;
    }

    if (Platform.OS === "web" && velocity && velocity.y === 0) {
      return;
    }

    const currentScrollDelta = y - previousScrollValue.value;

    smoothedScrollDeltaRef.current =
      SMOOTHING_FACTOR * smoothedScrollDeltaRef.current +
      (1 - SMOOTHING_FACTOR) * currentScrollDelta;

    const newScrollValue = clamp(
      scrollValue.value + smoothedScrollDeltaRef.current / scrollDistance,
      0,
      1
    );
    scrollValue.value = newScrollValue;
    if (y <= NEAR_TOP_THRESHOLD) {
      scrollValue.value = 0;
      binaryScrollValue.value = 0;
      smoothedScrollDeltaRef.current = 0;
    }

    if (
      Math.abs(smoothedScrollDeltaRef.current) >= CURRENT_SUSTAINED_THRESHOLD
    ) {
      binaryScrollValue.value = smoothedScrollDeltaRef.current > 0 ? 1 : 0;
    }

    if (
      Platform.OS === "web" &&
      y < NEAR_TOP_THRESHOLD &&
      (velocity?.y ?? 0) < 0
    ) {
      binaryScrollValue.value = 0;
    }

    const distanceFromBottom =
      contentSize.height / 2 < y + layoutMeasurement.height;

    if (distanceFromBottom && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      if (onNearEnd) {
        onNearEnd();
      }
    }

    if (contentSize.height !== previousContentHeightRef.current) {
      hasTriggeredRef.current = false;
      previousContentHeightRef.current = contentSize.height;
    }

    previousScrollValue.value = y;
    if (y !== 0 && routeKey === Routes.SearchResultScreen) {
      searchResultSavedOffset.value = y;
    }
  };

  const resetScrollState = () => {
    cancelAnimation(smoothedScrollDelta);
    cancelAnimation(yOffset);

    smoothedScrollDelta.value = 0;
    yOffset.value = 0;
    hasTriggered.value = false;
    smoothedScrollDeltaRef.current = 0;
    hasTriggeredRef.current = false;
  };

  const restoreFrameRef = useRef<number | null>(null);

  const scrollToOffset = (
    ref?: React.RefObject<any>,
    animated: boolean = false
  ) => {
    try {
      const targetOffset = searchResultSavedOffset.value;
      const shouldRestoreScroll = theme.isLargeScreen || theme.isTablet;
      if (!shouldRestoreScroll || !ref?.current || targetOffset == null) return;
      if (restoreFrameRef.current != null) {
        cancelAnimationFrame(restoreFrameRef.current);
      }
      restoreFrameRef.current = requestAnimationFrame(() => {
        restoreFrameRef.current = null;
        ref.current?.scrollToOffset?.({ offset: targetOffset, animated });
      });
      return () => {
        if (restoreFrameRef.current != null) {
          cancelAnimationFrame(restoreFrameRef.current);
          restoreFrameRef.current = null;
        }
      };
    } catch (e) {}
  };

  return {
    scrollHandler,
    yOffset,
    jsScrollHandler,
    resetScrollState,
    scrollToOffset,
  };
};

export default useHideNavsOnScroll;
